import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, Renderer2, ViewChild} from '@angular/core';
import {DynamicFormService} from '../../../dynamic-form/dynamic-form.service';
import {BaseFieldComponent} from '../../base/base-field/base-field.component';
import {EventDispatcherService} from '../../../events/event-dispatcher.service';
import {ChipsMultiSelectFieldConfig} from '../chips-multiselect-field.config';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import { ChipListMultiselectComponent, ChipListMultiselectItem, ChipListMultiselectOptions } from '@mydocma/component-library/src/lib/chips';

@UntilDestroy()
@Component({
    selector: 'mydocma-form-chips-multiselect-field',
    templateUrl: './chips-multiselect-field.component.html',
    styleUrls: ['./chips-multiselect-field.component.scss']
})
export class ChipsMultiSelectFieldComponent extends BaseFieldComponent<ChipsMultiSelectFieldConfig> implements OnInit, AfterViewInit {
    @ViewChild(ChipListMultiselectComponent)
    public chipList: ChipListMultiselectComponent;

    public readonly staticCssClass = 'mydocma-chips-multiselect-field';
    public items: ChipListMultiselectItem[] = [];
    public chipListOptions: ChipListMultiselectOptions = {};
    public expanded = false;
    public dropdownOpen = false;

    private source: ChipListMultiselectItem[] = [];
    private dropdownOrigin: HTMLElement;

    constructor(
        public readonly dynamicFormService: DynamicFormService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        public readonly eventDispatcher: EventDispatcherService,
        private readonly hostElement: ElementRef<HTMLElement>,
        renderer: Renderer2
    ) {
        super(dynamicFormService, changeDetectorRef, eventDispatcher, hostElement, renderer);
    }

    public async ngOnInit() {
        super.ngOnInit();

        this.control.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.syncItemsFromValue());

        this.syncOptions();
        await this.setSource();
    }

    public ngAfterViewInit(): void {
        this.dropdownOrigin = this.hostElement.nativeElement.querySelector('mat-form-field .mat-form-field-flex');
        this.syncOptions();
        this.changeDetectorRef.detectChanges();
        setTimeout(() => this.chipList?.recalculateOverflow());
    }

    public async onFieldWrapperUpdated() {
        super.onFieldWrapperUpdated();
        this.syncOptions();
        await this.setSource();
    }

    public onFieldExpandedChange(expanded: boolean): void {
        this.expanded = expanded;
        this.changeDetectorRef.markForCheck();
    }

    public onDropdownOpenChange(open: boolean): void {
        this.dropdownOpen = open;
        this.changeDetectorRef.detectChanges();
    }

    public onToggleDropdown(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.chipList?.toggleDropdown();
    }

    public get hasValue(): boolean {
        return Array.isArray(this.control?.value) && this.control.value.length > 0;
    }

    public get showDropdownArrow(): boolean {
        return !this.fieldWrapper.config.readonly && !this.fieldWrapper.config.disabled;
    }

    private async setSource(): Promise<void> {
        if (this.fieldWrapper.config.getSource) {
            this.source = await this.fieldWrapper.config.getSource();
        } else {
            this.source = this.fieldWrapper.config.source ?? [];
        }

        this.syncItemsFromValue();
    }

    private syncItemsFromValue(): void {
        const selected = Array.isArray(this.control?.value) ? this.control.value : [];
        const selectedIds = new Set(
            selected.map((item) => typeof item === 'string' ? item : item?.id)
        );

        this.items = this.source.map((item) => ({
            ...item,
            selected: selectedIds.has(item.id)
        }));
    }

    private syncOptions(): void {
        const configOptions = this.fieldWrapper.config.options ?? {};
        this.chipListOptions = {
            ...configOptions,
            readonly: !!this.fieldWrapper.config.readonly || !!this.fieldWrapper.config.disabled,
            chipDropdownOptions: {
                ...(configOptions.chipDropdownOptions ?? {}),
                customOrigin: configOptions.chipDropdownOptions?.customOrigin ?? this.dropdownOrigin
            }
        };
    }
}
